import React, { Component } from 'react'

import { graphql } from '@apollo/client/react/hoc'
import { Label, Alert, Input, Modal, ModalHeader, ModalBody, ModalFooter, Progress } from 'reactstrap'
import Select from 'react-select'
import { CreateBranchMutation } from "../Mutations"
import { AllBranches, AllCarDocumentTypesQuery, AllDriverDocumentTypesQuery, AllTenants } from "../Queries"
import toTitleCase from "../../Functions/toTitleCase"
import "./NewBranchModal.css"
import gql from 'graphql-tag'
import moment from "moment"

import withApolloClient from '../../withApolloClient'
import MapPicker from "../../Material/MapPicker"
import { TIMEZONES, COUNTRIES } from "../../Constants"
import { getStatesByCountry, getCitiesByState, getStateOptions, getCityOptions } from "../../utils"

const BranchDetailQuery = gql`query Branch($id: ID!){
    branch(id: $id){
        id
        name
        allowCcSurcharge
        requiresInsurance
        autoChargeCustomers
        autoDisableCustomers
        forTlc
        legalEntity
        defaultDeductible
        lineOfBusiness{
            id
            name
        }
        tenant {
            id
            name
        }
        driverDocTypes {
            edges {
                node {
                   id
                   typeName
                }
            }
        }
        driverDocsRequiredForRental {
            edges {
              node {
                id
              }
            }
          }
          driverDocsRequiredForApplication {
            edges {
              node {
                id
              }
            }
          }
          driverDocsRequiredForInsurance {
            edges {
              node {
                id
              }
            }
          }
          carDocsRequiredForRental {
            edges {
              node {
                id
              }
            }
          }
          carDocsRequiredForInspection {
            edges {
              node {
                id
              }
            }
          } 
          
    }
}`
const BranchSettingsQuery = gql`query allBranchSettings($branchId: ID!){
    allBranchSettings(branchId: $branchId){
        edges{
            node{
                id
                value
                key {
                    id
                    name
                    isRequired
                }
            }
        }
    }
}`

const BranchSettingKeyQuery = gql`query AllBranchSettingKeys{
    allBranchSettingKeys {
      edges {
        node {
          id
          name
          description
          type
          isRequired
        }
      }
    }
}`

const BranchLegalEntity = gql`
    query BranchLegalEntity{
      BranchLegalEntity:__type(name: "OrganizationBranchLegalEntityChoices") {
            values: enumValues {
                name
                description
            }
        }
    }`

const AllLineOfBusinessQuery = gql`
    query AllLineOfBusiness{
        allLineOfBusiness {
            edges {
                node {
                    id
                    name
                }
            }   
        }
    }
`

const STEP_DEFINITIONS = [
    { key: "branch", title: "Branch & Location", subtitle: "Step 1 of 4", icon: "pin" },
    { key: "configuration", title: "Configuration", subtitle: "Step 2 of 4", icon: "sliders" },
    { key: "documents", title: "Documents", subtitle: "Step 3 of 4", icon: "document" },
    { key: "settings", title: "Settings", subtitle: "Step 4 of 4", icon: "gear" },
]

const SETTING_LABEL_OVERRIDES = {
    EMAIL_FROM_DEFAULT: "Email From Default",
    SMS_FROM_DEFAULT: "SMS From Default",
    STRIPE_API_KEY: "Stripe App Key",
    STRIPE_PUBLIC_KEY: "Stripe Public Key",
    STRIPE_WEBHOOK_SECRET_KEY: "Stripe Webhook Secret Key",
    UNSCHEDULED_RETURN_FEE: "Unscheduled Return Fee",
    INSURANCE_AUTO_DENY_DAYS: "Insurance Auto Deny Days",
    INSURANCE_EXPIRATION_DAYS_WITHOUT_RENTAL: "Expiration Days Without Barrier",
    INSURANCE_EXPIRATION_DAYS_WITH_RENTAL: "Expiration Threshold",
    PREFERRED_TIMEZONE: "Preferred Timezone",
    ENABLE_MOBILE_APP_ARGYLE_BANNER: "Enable Mobile App Agree Banner",
    DEFAULT_BROKER_ID: "Default Broker",
    MINIMUM_DEPOSIT_REFUND_DAYS: "Enable Deposit Refund",
    SALES_TAX_RATE: "Sales Tax Rate",
}

const SETTING_PLACEHOLDER_OVERRIDES = {
    STRIPE_API_KEY: "sk_live_...",
    STRIPE_PUBLIC_KEY: "pk_live_...",
    STRIPE_WEBHOOK_SECRET_KEY: "whsec_...",
    INSURANCE_AUTO_DENY_DAYS: "e.g. 30",
    INSURANCE_EXPIRATION_DAYS_WITHOUT_RENTAL: "e.g. 7",
    INSURANCE_EXPIRATION_DAYS_WITH_RENTAL: "e.g. 14",
    UNSCHEDULED_RETURN_FEE: "e.g. 25.00",
    DEFAULT_BROKER_ID: "Choose broker",
    PREFERRED_TIMEZONE: "Select Timezone",
}

const getSettingLabel = (setting) => SETTING_LABEL_OVERRIDES[setting.name] || toTitleCase(setting.name)

const getSettingPlaceholder = (setting) => SETTING_PLACEHOLDER_OVERRIDES[setting.name] || setting.description || `Enter ${getSettingLabel(setting)}`

const getSelectValue = (options, value) => options.find((item) => item.value === value) || null

const hasPresentValue = (value) => value !== null && value !== undefined && value !== ""

const StepIcon = ({ kind, completed, active, variant = "circle" }) => {
    const getIconClass = () => {
        if (completed) return "fa fa-check"
        if (kind === "pin") return "fa fa-map-marker"
        if (kind === "sliders") return "fa fa-sliders"
        if (kind === "document") return "fa fa-file-o"
        if (kind === "gear") return "fa fa-cogs"
        if (kind === "building") return "fa fa-building-o"
        return "fa fa-info"
    }

    return (
        <span className={`new-branch-step-icon ${completed ? "is-complete" : ""} ${active ? "is-active" : ""} ${variant === "tab" ? "is-tab" : ""}`}>
            <i className={getIconClass()} aria-hidden="true" />
        </span>
    )
}

class NewBranchModal extends Component {
    constructor(props) {
        super(props)
        this.state = {
            name: null,
            customId: null,
            description: null,
            addressLine1: null,
            addressLine2: null,
            city: null,
            defaultDeductible: null,
            postalCode: null,
            country: "US",
            stateCode: null,
            errorMessage: "",
            validationErrors: {},
            loading: false,
            activeStep: 1,
            forTlc: false,
            requiresInsurance: false,
            allowCcSurcharge: false,
            autoChargeCustomers: true,
            autoDisableCustomers: false,
            driverRentalDoc: null,
            driverInsuranceDoc: null,
            driverApplicationDoc: null,
            driverDocTypes: null,
            carRentalDoc: null,
            carInspectionDoc: null,
            tenant: null,
            selectedBranch: null,
            addMoreSettings: false,
            lineOfBusinessId: null,
            legalEntity: null,
            activeDocumentSection: "driverApplicationDoc",
            documentSearchTerm: "",
            settings: {},
            errors: {
                driverRentalDoc: "",
                driverInsuranceDoc: "",
                driverApplicationDoc: "",
                driverDocTypes: "",
                carRentalDoc: "",
                carInspectionDoc: "",
                tenant: "",
                lineOfBusinessId: "",
                legalEntity: "",
                settings: {
                    SMS_FROM_DEFAULT: "",
                    EMAIL_FROM_DEFAULT: "",
                    STRIPE_API_KEY: "",
                    STRIPE_PUBLIC_KEY: "",
                    STRIPE_WEBHOOK_SECRET_KEY: "",
                    UNSCHEDULED_RETURN_FEE: "",
                    INSURANCE_AUTO_DENY_DAYS: "",
                    INSURANCE_EXPIRATION_DAYS_WITHOUT_RENTAL: "",
                    INSURANCE_EXPIRATION_DAYS_WITH_RENTAL: "",
                    PREFERRED_TIMEZONE: "",
                    INSURANCE_AUTO_DENY: "",
                    ENABLE_MOBILE_APP_ARGYLE_BANNER: "",
                    DEFAULT_BROKER_ID: "",
                    MINIMUM_DEPOSIT_REFUND_DAYS: "",
                    SALES_TAX_RATE: ""
                }
            }

        }
    }
    createBranch = (e) => {
        let firstInvalidStep = null
        const validationErrors = STEP_DEFINITIONS.reduce((errors, step, index) => {
            const stepErrors = this.getValidationErrorsForStep(index + 1)
            if (!firstInvalidStep && Object.keys(stepErrors).length > 0) {
                firstInvalidStep = index + 1
            }
            return { ...errors, ...stepErrors }
        }, {})

        if (Object.keys(validationErrors).length === 0) {
            try {
                this.setState({ loading: true, errorMessage: "", validationErrors: {} })
                let input = {
                    name: this.state.name,
                    tenantId: this.state.tenant,
                    customId: this.state.customId,
                    description: this.state.description,
                    defaultDeductible: parseFloat(this.state.defaultDeductible) ? parseFloat(this.state.defaultDeductible) : parseFloat(1000.00),
                    addressLine1: this.state.addressLine1,
                    addressLine2: this.state.addressLine2 || "",
                    city: this.state.city,
                    stateCode: this.state.stateCode,
                    postalCode: this.state.postalCode,
                    country: this.state.country,
                    forTlc: this.state.forTlc,
                    requiresInsurance: this.state.requiresInsurance,
                    allowCcSurcharge: this.state.allowCcSurcharge,
                    autoChargeCustomers: this.state.autoChargeCustomers,
                    autoDisableCustomers: this.state.autoDisableCustomers,
                    driverDocsRequiredForInsurance: this.state.driverInsuranceDoc,
                    driverDocsRequiredForRental: this.state.driverRentalDoc,
                    driverDocsRequiredForApplication: this.state.driverApplicationDoc,
                    driverDocTypes: this.state.driverDocTypes,
                    carDocsRequiredForRental: this.state.carRentalDoc,
                    carDocsRequiredForInspection: this.state.carInspectionDoc,
                    lineOfBusinessId: this.state.lineOfBusinessId,
                    legalEntity: this.state.legalEntity,
                    settings: this.getSettingsForSubmission()
                }
                this.props.apolloClient.mutate({
                    mutation: CreateBranchMutation,
                    variables: { input }
                }).then((result) => {
                    if (result && result.data && result.data.createBranch && result.data.createBranch.ok) {
                        this.setState({ errorMessage: "" })
                        this.props.handleSuccess()
                        this.props.handleClose()
                    } else {
                        let errorMessage = "An error occurred, could not complete request."
                        if (result.data && result.data.createBranch && result.data.createBranch.errors && result.data.createBranch.errors[0] && result.data.createBranch.errors[0].messages) {
                            errorMessage = result.data.createBranch.errors[0].messages.toString()
                        }
                        this.setState({ errorMessage: errorMessage })
                    }
                    this.setState({ loading: false })
                }).catch((err) => {
                    let errorMessage = "An error has occured " + err
                    this.setState({ errorMessage: errorMessage, loading: false })
                })
            }
            catch (err) {
                let errorMessage = "An error has occured " + err
                this.setState({ errorMessage: errorMessage, loading: false })
            }
        } else {
            this.setState({
                activeStep: firstInvalidStep || this.state.activeStep,
                errorMessage: "",
                validationErrors,
            })
        }
    }

    updateFields = (fields) => {
        this.setState((prevState) => {
            const validationErrors = { ...prevState.validationErrors }
            Object.keys(fields).forEach((field) => {
                if (hasPresentValue(fields[field])) {
                    delete validationErrors[field]
                }
            })
            return { ...fields, validationErrors }
        })
    }

    updateInput = (e) => {
        let name = e.target.name
        let value = e.target.value
        if (value === "null") {
            value = null
        }
        if (e.target.dataset.type) {
            if (value === "false") {
                value = false
            } else {
                value = true
            }
        }

        if (e.target.type === "number" && typeof value === "string" && !(name === "postalCode")) {
            value = parseFloat(value)
        }
        this.updateFields({ [name]: value })
    };

    updateSettingsInput = (e) => {
        const { name, value, type } = e.target
        const newValue = type === 'select-one' ? (value === 'true' ? true : value === 'false' ? false : null) : (value === 'null' ? null : value)
        this.setState(prevState => ({
            settings: {
                ...prevState.settings,
                [name]: String(newValue)
            },
            validationErrors: Object.keys(prevState.validationErrors).reduce((errors, field) => {
                if (field !== name || !hasPresentValue(newValue)) errors[field] = prevState.validationErrors[field]
                return errors
            }, {})
        }))
    };

    setActiveStep = (activeStep) => {
        this.setState({ activeStep })
    }

    goToNextStep = () => {
        const validationErrors = this.getValidationErrorsForStep()
        if (Object.keys(validationErrors).length > 0) {
            this.setState({ errorMessage: "", validationErrors })
            return
        }

        if (this.state.activeStep < STEP_DEFINITIONS.length) {
            this.setState({ errorMessage: "", validationErrors: {} })
            this.setActiveStep(this.state.activeStep + 1)
            return
        }

        this.createBranch()
    }

    goToPreviousStep = () => {
        if (this.state.activeStep > 1) {
            this.setActiveStep(this.state.activeStep - 1)
        }
    }

    toggleSettingBoolean = (name) => {
        this.setState((prevState) => ({
            settings: {
                ...prevState.settings,
                [name]: prevState.settings[name] === true ? false : true,
            },
            validationErrors: Object.keys(prevState.validationErrors).reduce((errors, field) => {
                if (field !== name) errors[field] = prevState.validationErrors[field]
                return errors
            }, {})
        }))
    }

    toggleListValue = (field, value) => {
        this.setState((prevState) => {
            const currentValues = Array.isArray(prevState[field]) ? prevState[field] : []
            const nextValues = currentValues.includes(value)
                ? currentValues.filter((item) => item !== value)
                : [...currentValues, value]

            const validationErrors = { ...prevState.validationErrors }
            if (nextValues.length > 0) delete validationErrors[field]

            return { [field]: nextValues, validationErrors }
        })
    }

    getSettingsFields = () => {
        const edges = this.props.allBranchSettingKeys && this.props.allBranchSettingKeys.edges ? this.props.allBranchSettingKeys.edges : []
        return edges.map((edge) => edge.node)
    }

    isBooleanSetting = (setting) => String(setting.type || "").toUpperCase() === "BOOLEAN"

    getSettingsForSubmission = () => {
        const settings = { ...this.state.settings }

        this.getSettingsFields()
            .filter((setting) => setting.isRequired && this.isBooleanSetting(setting))
            .forEach((setting) => {
                if (!hasPresentValue(settings[setting.id])) settings[setting.id] = "false"
            })

        return Object.entries(settings).map(([keyId, value]) => ({
            keyId,
            value: value === null || value === undefined ? "" : String(value),
        }))
    }

    hasRequiredBranchLocationInfo = () => (
        hasPresentValue(this.state.tenant) &&
        hasPresentValue(this.state.name) &&
        hasPresentValue(this.state.customId) &&
        hasPresentValue(this.state.description) &&
        hasPresentValue(this.state.addressLine1) &&
        hasPresentValue(this.state.country) &&
        hasPresentValue(this.state.stateCode) &&
        hasPresentValue(this.state.city) &&
        hasPresentValue(this.state.postalCode)
    )

    hasRequiredConfigurationInfo = () => (
        hasPresentValue(this.state.defaultDeductible) &&
        hasPresentValue(this.state.legalEntity) &&
        hasPresentValue(this.state.lineOfBusinessId)
    )

    hasRequiredDocumentInfo = () => (
        this.state.driverApplicationDoc && this.state.driverApplicationDoc.length > 0 &&
        this.state.driverRentalDoc && this.state.driverRentalDoc.length > 0 &&
        this.state.carRentalDoc && this.state.carRentalDoc.length > 0 &&
        this.state.carInspectionDoc && this.state.carInspectionDoc.length > 0 &&
        (!this.state.requiresInsurance || (this.state.driverInsuranceDoc && this.state.driverInsuranceDoc.length > 0))
    )

    hasRequiredBranchSettings = () => {
        const requiredFields = this.getSettingsFields().filter((field) => field.isRequired)

        return requiredFields.every((field) => (
            this.isBooleanSetting(field) || hasPresentValue(this.state.settings[field.id])
        ))
    }

    getValidationErrorsForStep = (step = this.state.activeStep) => {
        const validationErrors = {}
        const requireField = (field, value) => {
            if (!hasPresentValue(value)) validationErrors[field] = "This field is required."
        }

        if (step === 1) {
            requireField("tenant", this.state.tenant)
            requireField("name", this.state.name)
            requireField("customId", this.state.customId)
            requireField("description", this.state.description)
            requireField("addressLine1", this.state.addressLine1)
            requireField("country", this.state.country)
            requireField("stateCode", this.state.stateCode)
            requireField("city", this.state.city)
            requireField("postalCode", this.state.postalCode)
        }

        if (step === 2) {
            requireField("defaultDeductible", this.state.defaultDeductible)
            requireField("legalEntity", this.state.legalEntity)
            requireField("lineOfBusinessId", this.state.lineOfBusinessId)
        }

        if (step === 3) {
            requireField("driverApplicationDoc", this.state.driverApplicationDoc && this.state.driverApplicationDoc.length > 0 ? "selected" : "")
            requireField("driverRentalDoc", this.state.driverRentalDoc && this.state.driverRentalDoc.length > 0 ? "selected" : "")
            requireField("carRentalDoc", this.state.carRentalDoc && this.state.carRentalDoc.length > 0 ? "selected" : "")
            requireField("carInspectionDoc", this.state.carInspectionDoc && this.state.carInspectionDoc.length > 0 ? "selected" : "")
            if (this.state.requiresInsurance) {
                requireField("driverInsuranceDoc", this.state.driverInsuranceDoc && this.state.driverInsuranceDoc.length > 0 ? "selected" : "")
            }
        }

        if (step === 4) {
            this.getSettingsFields()
                .filter((field) => field.isRequired && !this.isBooleanSetting(field))
                .forEach((field) => requireField(field.id, this.state.settings[field.id]))
        }

        return validationErrors
    }

    canProceedFromCurrentStep = () => {
        if (this.state.activeStep === 1) {
            return this.hasRequiredBranchLocationInfo()
        }

        if (this.state.activeStep === 2) {
            return this.hasRequiredConfigurationInfo()
        }

        if (this.state.activeStep === 3) {
            return this.hasRequiredDocumentInfo()
        }

        return this.hasRequiredBranchSettings() && this.hasRequiredBranchLocationInfo() && this.hasRequiredConfigurationInfo() && this.hasRequiredDocumentInfo()
    }

    renderFormField = (label, control, options = {}) => {
        const { compact = false, className = "", description = null, error = "" } = options
        return (
            <div className={`new-branch-field ${compact ? "is-compact" : ""} ${error ? "is-invalid" : ""} ${className}`.trim()}>
                <div className="new-branch-field-label-row">
                    <Label className="new-branch-field-label">{label}</Label>
                    {description}
                </div>
                {control}
                {error && <div className="new-branch-field-error">{error}</div>}
            </div>
        )
    }

    renderSelect = (label, props) => {
        const { compact = false, description = null, className = "", error = "", ...selectProps } = props
        return this.renderFormField(
            label,
            <Select
                className={`bos-custom-select new-branch-select ${error ? "is-invalid" : ""} ${className}`.trim()}
                classNamePrefix="bos-select"
                {...selectProps}
            />,
            { compact, description, className, error }
        )
    }

    renderInput = (label, props) => {
        const { compact = false, description = null, className = "", error = "", ...inputProps } = props
        return this.renderFormField(
            label,
            <Input className={`new-branch-input ${error ? "is-invalid" : ""} ${className}`.trim()} {...inputProps} />,
            { compact, description, className, error }
        )
    }

    renderTextarea = (label, props) => this.renderInput(label, { ...props, type: "textarea" })

    renderToggleCard = (label, description, checked, onChange) => (
        <div className="new-branch-toggle-card">
            <div className="new-branch-toggle-copy">
                <div className="new-branch-toggle-label">{label}</div>
                <div className="new-branch-toggle-description">{description}</div>
            </div>
            <button
                type="button"
                className={`new-branch-toggle ${checked ? "is-on" : ""}`}
                aria-pressed={checked}
                onClick={onChange}
            >
                <span className="new-branch-toggle-knob" />
            </button>
        </div>
    )

    getDocumentSections = () => ([
        {
            key: "driverApplicationDoc",
            title: "Driver Docs — Application",
            description: "Required when a driver applies",
            optionsType: "driver",
            field: "driverApplicationDoc",
            enabled: true,
        },
        {
            key: "driverRentalDoc",
            title: "Driver Docs — Rental",
            description: "Required when a driver rents a vehicle",
            optionsType: "driver",
            field: "driverRentalDoc",
            enabled: true,
        },
        {
            key: "driverInsuranceDoc",
            title: "Driver Docs — Insurance",
            description: "Required for insurance enrollment",
            optionsType: "driver",
            field: "driverInsuranceDoc",
            enabled: this.state.requiresInsurance,
        },
        {
            key: "carRentalDoc",
            title: "Car Docs — Rental",
            description: "Required at the time of rental",
            optionsType: "car",
            field: "carRentalDoc",
            enabled: true,
        },
        {
            key: "carInspectionDoc",
            title: "Car Docs — Inspection",
            description: "Required for vehicle inspection",
            optionsType: "car",
            field: "carInspectionDoc",
            enabled: true,
        },
    ])

    getDocumentOptionsForType = (optionsType) => {
        if (optionsType === "driver") {
            return this.props.allDriverDocumentTypes && this.props.allDriverDocumentTypes.edges && this.props.allDriverDocumentTypes.edges.length > 0
                ? this.props.allDriverDocumentTypes.edges.map((docType) => ({ label: docType.node.typeName, value: docType.node.id }))
                : []
        }

        return this.props.allCarDocumentTypes && this.props.allCarDocumentTypes.edges && this.props.allCarDocumentTypes.edges.length > 0
            ? this.props.allCarDocumentTypes.edges.map((docType) => ({ label: docType.node.typeName, value: docType.node.id }))
            : []
    }

    getDocumentSelectedIds = (field) => (Array.isArray(this.state[field]) ? this.state[field] : [])

    setDocumentSection = (sectionKey) => {
        this.setState((prevState) => ({
            activeDocumentSection: prevState.activeDocumentSection === sectionKey ? null : sectionKey,
            documentSearchTerm: "",
        }))
    }

    setDocumentSearchTerm = (value) => {
        this.setState({ documentSearchTerm: value })
    }

    renderDocumentOption = (label, value, checked, onChange, disabled = false) => (
        <button
            type="button"
            className={`new-branch-document-option ${checked ? "is-checked" : ""} ${disabled ? "is-disabled" : ""}`}
            onClick={() => !disabled && onChange(value)}
            disabled={disabled}
        >
            <span className={`new-branch-document-check ${checked ? "is-checked" : ""}`} />
            <span className="new-branch-document-label">{label}</span>
        </button>
    )

    renderDocumentAccordion = (section) => {
        const options = this.getDocumentOptionsForType(section.optionsType)
        const selectedIds = this.getDocumentSelectedIds(section.field)
        const selectedLabels = options.filter((option) => selectedIds.includes(option.value)).map((option) => option.label)
        const searchValue = this.state.documentSearchTerm || ""
        const normalizedSearch = searchValue.trim().toLowerCase()
        const filteredOptions = normalizedSearch
            ? options.filter((option) => option.label.toLowerCase().includes(normalizedSearch))
            : options
        const sortedOptions = [...filteredOptions].sort((firstOption, secondOption) => (
            Number(selectedIds.includes(secondOption.value)) - Number(selectedIds.includes(firstOption.value))
        ))
        const totalSelected = selectedIds.length
        const isOpen = this.state.activeDocumentSection === section.key
        const error = this.state.validationErrors[section.field] || ""

        return (
            <div key={section.key} className={`new-branch-document-accordion ${isOpen ? "is-open" : ""} ${error ? "is-invalid" : ""} ${!section.enabled ? "is-disabled" : ""}`}>
                <button
                    type="button"
                    className="new-branch-document-accordion-header"
                    onClick={() => this.setDocumentSection(section.key)}
                >
                    <div className="new-branch-document-accordion-title-row">
                        <span className="new-branch-document-accordion-title">{section.title}</span>
                        {totalSelected > 0 && <span className="new-branch-document-accordion-count">{totalSelected}</span>}
                    </div>
                    <span className="new-branch-document-accordion-chevron" />
                </button>
                {error && <div className="new-branch-field-error new-branch-document-error">{error}</div>}

                {isOpen && (
                    <div className="new-branch-document-accordion-body">
                        <div className="new-branch-document-accordion-subtitle">{section.description}</div>
                        {selectedLabels.length > 0 && (
                            <div className="new-branch-document-selected-row">
                                {selectedLabels.map((label) => (
                                    <span key={label} className="new-branch-document-selected-pill">{label}</span>
                                ))}
                            </div>
                        )}
                        <div className="new-branch-document-search-row">
                            <Input
                                type="text"
                                className="new-branch-document-search"
                                placeholder="Search documents..."
                                value={this.state.documentSearchTerm || ""}
                                onChange={(e) => this.setDocumentSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="new-branch-document-grid">
                            {sortedOptions.map((option) => this.renderDocumentOption(
                                option.label,
                                option.value,
                                selectedIds.includes(option.value),
                                (value) => this.toggleListValue(section.field, value),
                                !section.enabled
                            ))}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    renderSettingsField = (setting) => {
        const label = `${getSettingLabel(setting)}${setting.isRequired ? " *" : " (Optional)"}`
        const value = this.state.settings[setting.id] !== undefined ? this.state.settings[setting.id] : ""
        const placeholder = getSettingPlaceholder(setting)
        const settingType = (setting.type || "").toString().toUpperCase()
        const isBoolean = settingType === "BOOLEAN"
        const isTimezone = setting.name === "PREFERRED_TIMEZONE"
        const error = isBoolean ? "" : (this.state.validationErrors[setting.id] || "")

        if (isBoolean) {
            const checked = value === true || value === "true"
            return (
                <div key={setting.id} className={`new-branch-settings-card is-toggle ${error ? "is-invalid" : ""}`}>
                    <div className="new-branch-settings-card-copy">
                        <div className="new-branch-settings-card-title">{label}</div>
                        <div className="new-branch-settings-card-subtitle">{setting.description}</div>
                        {error && <div className="new-branch-field-error">{error}</div>}
                    </div>
                    <button
                        type="button"
                        className={`new-branch-toggle ${checked ? "is-on" : ""}`}
                        aria-pressed={checked}
                        onClick={() => this.toggleSettingBoolean(setting.id)}
                    >
                        <span className="new-branch-toggle-knob" />
                    </button>
                </div>
            )
        }

        if (isTimezone) {
            const timezoneOptions = TIMEZONES.map((zone) => ({
                value: zone.value,
                label: `${moment.tz(zone.value).format('zz')} | ${zone.label}`,
            }))

            return this.renderSelect(label, {
                key: setting.id,
                compact: true,
                placeholder,
                error,
                options: timezoneOptions,
                value: getSelectValue(timezoneOptions, value),
                onChange: (selected) => {
                    const selectedValue = selected ? selected.value : ""
                    this.setState((prevState) => {
                        const validationErrors = { ...prevState.validationErrors }
                        if (hasPresentValue(selectedValue)) delete validationErrors[setting.id]
                        return {
                            settings: {
                                ...prevState.settings,
                                [setting.id]: selectedValue,
                            },
                            validationErrors,
                        }
                    })
                },
            })
        }

        return this.renderInput(label, {
            key: setting.id,
            compact: true,
            error,
            type: ["FLOAT", "INTEGER", "INT", "NUMBER", "DECIMAL"].includes(settingType) ? "number" : "text",
            placeholder,
            value: value,
            onChange: this.updateSettingsInput,
            name: setting.id,
            id: setting.id,
        })
    }

    getRequiredBranchFieldsMissing = () => {
        return !(
            this.hasRequiredBranchLocationInfo() &&
            this.hasRequiredConfigurationInfo() &&
            this.hasRequiredDocumentInfo() &&
            this.hasRequiredBranchSettings()
        )
    }

    renderSectionHeader = (title, subtitle) => (
        <div className="new-branch-section-header">
            <div className="new-branch-section-title">{title}</div>
            {subtitle ? <div className="new-branch-section-subtitle">{subtitle}</div> : null}
        </div>
    )

    renderSectionKicker = (label) => (
        <div className="new-branch-section-kicker">{label}</div>
    )

    renderTabIcon = (step, active, completed) => (
        <StepIcon kind={step.icon} active={active} completed={completed} variant="tab" />
    )

    renderStepTabs = () => (
        <div className="new-branch-tabs">
            {STEP_DEFINITIONS.map((step, index) => {
                const stepNumber = index + 1
                const active = this.state.activeStep === stepNumber
                const completed = this.state.activeStep > stepNumber
                return (
                    <button
                        type="button"
                        key={step.key}
                        className={`new-branch-tab ${active ? "is-active" : ""} ${completed ? "is-complete" : ""}`}
                        onClick={() => this.setActiveStep(stepNumber)}
                        disabled={stepNumber > this.state.activeStep}
                    >
                        {completed ? <span className="new-branch-tab-check">✓</span> : this.renderTabIcon(step, active, completed)}
                        <span className="new-branch-tab-label">{step.title}</span>
                    </button>
                )
            })}
        </div>
    )

    renderProgressDots = () => (
        <div className="new-branch-progress-dots" aria-hidden="true">
            {STEP_DEFINITIONS.map((step, index) => {
                const stepNumber = index + 1
                const active = this.state.activeStep === stepNumber
                return <span key={step.key} className={`new-branch-progress-dot ${active ? "is-active" : ""}`} />
            })}
        </div>
    )

    renderStepFooter = (isFinalStep = false) => (
        <>
            <button type="button" className="new-branch-footer-link" onClick={this.goToPreviousStep} disabled={this.state.activeStep === 1}>
                <span className="new-branch-footer-chevron is-left" />
                Previous
            </button>
            {this.renderProgressDots()}
            <button
                type="button"
                className="new-branch-primary-btn"
                onClick={isFinalStep ? this.createBranch : this.goToNextStep}
                disabled={this.state.loading || this.props.loading}
            >
                {isFinalStep && <span className="new-branch-footer-check">✓</span>}
                {isFinalStep ? "Add Branch" : "Continue"}
                {!isFinalStep && <span className="new-branch-footer-chevron" />}
            </button>
        </>
    )

    renderBranchLocationStep = () => {
        const stateOptions = getStateOptions(getStatesByCountry(this.state.country))
        const cityOptions = getCityOptions(getCitiesByState(this.state.stateCode, this.state.country))

        if (this.state.city && !getSelectValue(cityOptions, this.state.city)) {
            cityOptions.unshift({ value: this.state.city, label: this.state.city })
        }

        const countryOptions = COUNTRIES.map((country) => ({ value: country.value, label: country.label }))

        return (
            <div className="new-branch-field-stack">
                <div className="new-branch-three-column">
                    {this.renderSelect("Tenant *", {
                        isLoading: this.props.tenantsLoading,
                        options: this.props.allTenants && this.props.allTenants.edges && this.props.allTenants.edges.length > 0 ? this.props.allTenants.edges.map((tenant) => ({ label: tenant.node.name, value: tenant.node.id })) : [],
                        placeholder: "Select a tenant",
                        error: this.state.validationErrors.tenant,
                        onChange: (tenant) => this.updateFields({ tenant: tenant ? tenant.value : null }),
                        value: this.props.allTenants && this.props.allTenants.edges && this.props.allTenants.edges.length > 0 ? getSelectValue(this.props.allTenants.edges.map((tenant) => ({ label: tenant.node.name, value: tenant.node.id })), this.state.tenant) : null,
                    })}
                    {this.renderInput("Branch Name *", {
                        name: "name",
                        id: "name",
                        placeholder: "e.g. Miami Downtown",
                        value: this.state.name || "",
                        onChange: this.updateInput,
                        error: this.state.validationErrors.name,
                    })}
                    {this.renderInput("Custom ID *", {
                        name: "customId",
                        id: "customId",
                        placeholder: "e.g. MAKE_FL",
                        value: this.state.customId || "",
                        onChange: this.updateInput,
                        error: this.state.validationErrors.customId,
                    })}
                </div>
                {/* <div className="new-branch-body-divider" /> */}
                {/* <div className="new-branch-section-kicker">LOCATION</div> */}
                <div className={`new-branch-field mb-2 ${this.state.validationErrors.addressLine1 ? "is-invalid" : ""}`}>
                    <Label className="new-branch-field-label mb-2">Branch Address *</Label>
                    <MapPicker
                        initialLocation={this.state.addressLine1 || ""}
                        onLocationSelect={(loc) => {
                            const addressLine1 = loc.address ? loc.address.split(",")[0].trim() : ""
                            this.updateFields({
                                addressLine1,
                                country: loc.country || "",
                                stateCode: loc.state || "",
                                city: loc.city || "",
                                postalCode: loc.postalCode || "",
                            })
                        }}
                    />
                    {this.state.validationErrors.addressLine1 && <div className="new-branch-field-error">{this.state.validationErrors.addressLine1}</div>}
                </div>
                <div className="new-branch-four-column">
                    {this.renderSelect("Country *", {
                        options: countryOptions,
                        placeholder: "US",
                        error: this.state.validationErrors.country,
                        value: this.state.country ? getSelectValue(countryOptions || [], this.state.country) : null,
                        onChange: (country) => this.updateFields({ country: country ? country.value : null, stateCode: "", city: "" }),
                    })}
                    {this.renderSelect("State *", {
                        options: stateOptions || [],
                        placeholder: "Select state",
                        isDisabled: !this.state.country,
                        error: this.state.validationErrors.stateCode,
                        value: this.state.stateCode ? getSelectValue(stateOptions || [], this.state.stateCode) : null,
                        onChange: (selectedState) => this.updateFields({ stateCode: selectedState ? selectedState.value : null, city: "" }),
                    })}
                    {this.renderSelect("City *", {
                        options: cityOptions || [],
                        placeholder: "Select city",
                        isDisabled: !this.state.stateCode,
                        error: this.state.validationErrors.city,
                        value: this.state.city ? getSelectValue(cityOptions || [], this.state.city) : null,
                        onChange: (selectedCity) => this.updateFields({ city: selectedCity ? selectedCity.value : null }),
                    })}
                    {this.renderInput("Postal Code *", {
                        type: "text",
                        name: "postalCode",
                        id: "postalCode",
                        placeholder: "33101",
                        value: this.state.postalCode || "",
                        onChange: this.updateInput,
                        error: this.state.validationErrors.postalCode,
                    })}
                </div>
                {this.renderTextarea("Description *", {
                    name: "description",
                    id: "description",
                    placeholder: "Enter a description for this branch...",
                    value: this.state.description || "",
                    onChange: this.updateInput,
                    error: this.state.validationErrors.description,
                })}
            </div>
        )
    }

    renderConfigurationStep = () => {
        const legalEntityOptions = this.props.BranchLegalEntity && this.props.BranchLegalEntity.values && this.props.BranchLegalEntity.values.length > 0 ? this.props.BranchLegalEntity.values.map((item) => ({ value: item.name, label: item.description })) : []
        const lineOfBusinessOptions = this.props.allLineOfBusiness && this.props.allLineOfBusiness.edges && this.props.allLineOfBusiness.edges.length > 0 ? this.props.allLineOfBusiness.edges.map((item) => ({ value: item.node.id, label: item.node.name })) : []

        return (
            <div className="new-branch-field-stack">
                <div className="new-branch-section-card">
                    <div className="new-branch-section-title">Clone Settings From Branch</div>
                    <div className="new-branch-section-subtitle mb-2">Inherit settings from an existing branch to speed up setup.</div>
                    <Select
                        className="bos-custom-select new-branch-select"
                        classNamePrefix="bos-select"
                        isLoading={this.props.branchesLoading}
                        options={this.props.allBranches && this.props.allBranches.edges && this.props.allBranches.edges.length > 0 ? this.props.allBranches.edges.map((branch) => ({ label: branch.node.name, value: branch.node.id })) : []}
                        onChange={(branch) => branch && this.getBranchAttributes(branch.value)}
                    // placeholder="Miami North"
                    />
                </div>
                <div className="new-branch-three-column">
                    {this.renderInput("Collision Deductible *", {
                        type: "number",
                        name: "defaultDeductible",
                        id: "defaultDeductible",
                        placeholder: "$0",
                        value: this.state.defaultDeductible || "",
                        onChange: this.updateInput,
                        error: this.state.validationErrors.defaultDeductible,
                    })}
                    {this.renderSelect("Legal Entity *", {
                        options: legalEntityOptions,
                        placeholder: "Corporation",
                        error: this.state.validationErrors.legalEntity,
                        value: getSelectValue(legalEntityOptions, this.state.legalEntity),
                        onChange: (legalEntity) => this.updateFields({ legalEntity: legalEntity ? legalEntity.value : null }),
                    })}
                    {this.renderSelect("Line of Business *", {
                        options: lineOfBusinessOptions,
                        placeholder: "Choose line",
                        error: this.state.validationErrors.lineOfBusinessId,
                        value: getSelectValue(lineOfBusinessOptions, this.state.lineOfBusinessId),
                        onChange: (lineOfBusiness) => this.updateFields({ lineOfBusinessId: lineOfBusiness ? lineOfBusiness.value : null }),
                    })}
                </div>

                {this.renderSectionKicker("PAYMENT & POLICY")}
                <div className="new-branch-toggle-grid">
                    {this.renderToggleCard("For TLC", "For Taxi & Limousine Commission", this.state.forTlc, () => this.setState((prevState) => ({ forTlc: !prevState.forTlc })))}
                    {this.renderToggleCard("CC Surcharge", "Apply credit card surcharge", this.state.allowCcSurcharge, () => this.setState((prevState) => ({ allowCcSurcharge: !prevState.allowCcSurcharge })))}
                    {this.renderToggleCard("Auto Charge Customers", "Charge customers on due date", this.state.autoChargeCustomers, () => this.setState((prevState) => ({ autoChargeCustomers: !prevState.autoChargeCustomers })))}
                    {this.renderToggleCard("Insurance Required", "Require insurance verification", this.state.requiresInsurance, () => this.setState((prevState) => ({ requiresInsurance: !prevState.requiresInsurance })))}
                </div>
            </div>
        )
    }

    renderDocumentsStep = () => {
        const sections = this.getDocumentSections()
        const totalSelected = sections.reduce((sum, section) => sum + this.getDocumentSelectedIds(section.field).length, 0)

        return (
            <div className="new-branch-field-stack">
                <div className="new-branch-document-intro-row">
                    <div className="new-branch-section-subtitle">Expand a category to search and select required documents. Each category can hold any number of documents.</div>
                    <div className="new-branch-document-total">{totalSelected} selected total</div>
                </div>
                <div className="new-branch-document-accordion-list">
                    {sections.map((section) => this.renderDocumentAccordion(section))}
                </div>
            </div>
        )
    }

    renderSettingsGroup = (title, subtitle, fields, gridClassName = "") => (
        <div className="new-branch-settings-group">
            <div className="new-branch-section-kicker">{title}</div>
            {subtitle ? <div className="new-branch-section-subtitle">{subtitle}</div> : null}
            <div className={`new-branch-settings-grid ${gridClassName} ${title === "INSURANCE & FEES" ? "is-four" : ""}`.trim()}>
                {fields.map((field) => (
                    <React.Fragment key={field.id}>
                        {this.renderSettingsField(field)}
                    </React.Fragment>
                ))}
            </div>
        </div>
    )

    renderSettingsStep = () => {
        const allFields = this.getSettingsFields()
        const requiredFields = allFields.filter((field) => field.isRequired)
        const optionalFields = allFields.filter((field) => !field.isRequired)
        const isBooleanField = (field) => String(field.type || "").toUpperCase() === "BOOLEAN"
        const requiredToggleFields = requiredFields.filter(isBooleanField)
        const requiredInputFields = requiredFields.filter((field) => !isBooleanField(field))
        const optionalToggleFields = optionalFields.filter(isBooleanField)
        const optionalInputFields = optionalFields.filter((field) => !isBooleanField(field))

        return (
            <div className="new-branch-field-stack">
                {requiredInputFields.length > 0 && (
                    <div className="new-branch-settings-section">
                        {this.renderSettingsGroup("BRANCH SETTINGS", "", requiredInputFields)}
                    </div>
                )}

                {requiredToggleFields.length > 0 && (
                    <div className="new-branch-settings-section">
                        <div className="new-branch-section-kicker">REQUIRED OPTIONS</div>
                        <div className="new-branch-settings-toggle-grid">
                            {requiredToggleFields.map((field) => (
                                <React.Fragment key={field.id}>
                                    {this.renderSettingsField(field)}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                )}

                {optionalFields.length > 0 && (
                    <div className="new-branch-add-more-row">
                        <div>
                            <div className="new-branch-add-more-title">Additional settings</div>
                            <div className="new-branch-section-subtitle">Configure optional branch workflows, integrations, alerts, and limits.</div>
                        </div>
                        <button
                            type="button"
                            className="new-branch-add-more-btn"
                            aria-expanded={this.state.addMoreSettings}
                            onClick={() => this.setState((prevState) => ({ addMoreSettings: !prevState.addMoreSettings }))}
                        >
                            <i className={`fa fa-${this.state.addMoreSettings ? "minus" : "plus"}`} aria-hidden="true" />
                            {this.state.addMoreSettings ? "Hide Additional Settings" : "Add More Settings"}
                        </button>
                    </div>
                )}

                {this.state.addMoreSettings && optionalFields.length > 0 && (
                    <div className="new-branch-settings-section">
                        <div className="new-branch-section-kicker">ADDITIONAL SETTINGS</div>
                        {optionalToggleFields.length > 0 && (
                            <div className="new-branch-settings-toggle-grid">
                                {optionalToggleFields.map((field) => (
                                    <React.Fragment key={field.id}>
                                        {this.renderSettingsField(field)}
                                    </React.Fragment>
                                ))}
                            </div>
                        )}
                        {optionalInputFields.length > 0 && this.renderSettingsGroup("OPTIONAL INPUTS", "", optionalInputFields)}
                    </div>
                )}
            </div>
        )
    }

    renderActiveStep = () => {
        if (this.state.activeStep === 1) {
            return this.renderBranchLocationStep()
        }

        if (this.state.activeStep === 2) {
            return this.renderConfigurationStep()
        }

        if (this.state.activeStep === 3) {
            return this.renderDocumentsStep()
        }

        return this.renderSettingsStep()
    }

    pObj = (jsonString) => {
        try {
            return JSON.parse(jsonString)
        } catch (error) {
            return {}
        }
    }

    getBranchSettings = (id) => {
        this.setState({ loading: true, selectedBranch: id })
        this.props.apolloClient.query({
            query: BranchSettingsQuery,
            variables: { branchId: id }
        }).then((result) => {
            this.setState({ loading: false })
            if (result.data && result.data.allBranchSettings && result.data.allBranchSettings.edges && result.data.allBranchSettings.edges.length > 0) {
                let branchSettingsEntries = []
                result.data.allBranchSettings.edges.map(item => {
                    branchSettingsEntries.push({ keyId: item.node.key.id, value: item.node.value })
                })
                let defaultSettings = this.props.allBranchSettingKeys.edges.filter(item => item.node.isRequired).map((key) => {
                    let matchedKey = branchSettingsEntries.find(({ keyId }) => keyId === key.node.id)
                    let value = matchedKey ? matchedKey.value : null
                    return {
                        [key.node.id]: value ? value : null,
                    }
                })
                let defaultNullSettings = defaultSettings.filter(item => Object.keys(item).filter(key => item[key] === null).length > 0).map(item => Object.keys(item)).flat()
                let requiredSettings = this.props.allBranchSettingKeys.edges.filter(item => item.node.isRequired).filter(item => defaultNullSettings.includes(item.node.id)).map(item => ({ [item.node.name]: "This Field is Required" })).flat()
                this.setState((prevState) => ({
                    settings: {
                        ...prevState.settings,
                        ...Object.assign({}, ...defaultSettings),
                    },
                    errors: {
                        ...prevState.errors,
                        settings: {
                            ...prevState.errors.settings,
                            ...Object.assign({}, requiredSettings.reduce((acc, item) => ({ ...acc, ...item }), {}))
                        }
                    }
                }))
            } else {
                this.props.allBranchSettingKeys.edges.forEach((key) => {
                    this.setState((prevState) => ({
                        errors: {
                            ...prevState.errors,
                            settings: {
                                ...prevState.errors.settings,
                                [key.node.name]: "This Field is Required"
                            }
                        }
                    }))
                })
            }
        })
    }
    getBranchAttributes = (id) => {
        this.setState({ loading: true, selectedBranch: id })
        this.props.apolloClient.query({
            query: BranchDetailQuery,
            variables: { id: id }
        }).then((result) => {
            this.setState({ loading: false })
            if (result.data && result.data.branch) {
                this.getBranchSettings(id)
                let docs = {
                    "driverRentalDoc": [],
                    "driverInsuranceDoc": [],
                    "driverApplicationDoc": [],
                    "carRentalDoc": [],
                    "carInspectionDoc": [],
                    "driverDocTypes": []
                }
                if (result.data.branch.driverDocTypes && result.data.branch.driverDocTypes.edges && result.data.branch.driverDocTypes.edges.length > 0) {
                    result.data.branch.driverDocTypes.edges.map(item => {
                        docs.driverDocTypes.push(item.node.id)
                    })
                }
                if (result.data.branch.driverDocsRequiredForRental && result.data.branch.driverDocsRequiredForRental.edges && result.data.branch.driverDocsRequiredForRental.edges.length > 0) {
                    result.data.branch.driverDocsRequiredForRental.edges.map(item => {
                        docs.driverRentalDoc.push(item.node.id)
                    })
                }
                if (result.data.branch.driverDocsRequiredForInsurance && result.data.branch.driverDocsRequiredForInsurance.edges && result.data.branch.driverDocsRequiredForInsurance.edges.length > 0) {
                    result.data.branch.driverDocsRequiredForInsurance.edges.map(item => {
                        docs.driverInsuranceDoc.push(item.node.id)
                    })
                }
                if (result.data.branch.driverDocsRequiredForApplication && result.data.branch.driverDocsRequiredForApplication.edges && result.data.branch.driverDocsRequiredForApplication.edges.length > 0) {
                    result.data.branch.driverDocsRequiredForApplication.edges.map(item => {
                        docs.driverApplicationDoc.push(item.node.id)
                    })
                }
                if (result.data.branch.carDocsRequiredForRental && result.data.branch.carDocsRequiredForRental.edges && result.data.branch.carDocsRequiredForRental.edges.length > 0) {
                    result.data.branch.carDocsRequiredForRental.edges.map(item => {
                        docs.carRentalDoc.push(item.node.id)
                    })
                }
                if (result.data.branch.carDocsRequiredForInspection && result.data.branch.carDocsRequiredForInspection.edges && result.data.branch.carDocsRequiredForInspection.edges.length > 0) {
                    result.data.branch.carDocsRequiredForInspection.edges.map(item => {
                        docs.carInspectionDoc.push(item.node.id)
                    })
                }
                let emptyDocs = {}
                Object.keys(docs).forEach(key => {
                    if (docs[key].length === 0) {
                        emptyDocs[key] = "This field is required"
                    }
                })
                this.setState({
                    forTlc: result.data.branch.forTlc,
                    autoChargeCustomers: result.data.branch.autoChargeCustomers,
                    autoDisableCustomers: result.data.branch.autoDisableCustomers,
                    allowCcSurcharge: result.data.branch.allowCcSurcharge,
                    requiresInsurance: result.data.branch.requiresInsurance,
                    driverRentalDoc: docs.driverRentalDoc,
                    driverInsuranceDoc: docs.driverInsuranceDoc,
                    driverApplicationDoc: docs.driverApplicationDoc,
                    carRentalDoc: docs.carRentalDoc,
                    carInspectionDoc: docs.carInspectionDoc,
                    driverDocTypes: docs.driverDocTypes,
                    legalEntity: result.data.branch.legalEntity !== null ? result.data.branch.legalEntity : "",
                    lineOfBusinessId: result.data.branch.lineOfBusiness ? result.data.branch.lineOfBusiness.id : "",
                    defaultDeductible: result.data.branch.defaultDeductible,
                    errors: {
                        ...this.state.errors,
                        ...emptyDocs,
                        legalEntity: result.data.branch.legalEntity ? "" : "This field is required",
                        lineOfBusinessId: result.data.branch.lineOfBusinessId ? "" : "This field is required",
                    }
                })
            }
        })
    }

    componentDidMount() {
        // no-op
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.open && this.props.open) {
            const nextState = {}
            if (this.state.activeStep !== 1) {
                nextState.activeStep = 1
            }
            if (this.state.errorMessage) {
                nextState.errorMessage = ""
            }
            nextState.validationErrors = {}
            nextState.activeDocumentSection = "driverApplicationDoc"
            nextState.documentSearchTerm = ""
            nextState.addMoreSettings = false
            if (Object.keys(nextState).length > 0) {
                this.setState(nextState)
            }
        }
    }

    render() {
        const isFinalStep = this.state.activeStep === STEP_DEFINITIONS.length
        const loading = this.state.loading || this.props.loading
        const activeStep = STEP_DEFINITIONS[this.state.activeStep - 1] || STEP_DEFINITIONS[0]

        return (
            <Modal isOpen={this.props.open} className="new-branch-modal" centered>
                <ModalHeader toggle={this.props.handleClose} className="new-branch-modal-header">
                    <div className="new-branch-header-left">
                        <div className="new-branch-header-badge">
                            <StepIcon kind="building" active completed={false} />
                        </div>
                        <div className="new-branch-header-copy">
                            <div className="new-branch-header-title">Add New Branch</div>
                            <div className="new-branch-header-subtitle">Step {this.state.activeStep} of {STEP_DEFINITIONS.length} — {activeStep.title}</div>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="new-branch-modal-body">
                    <div className="new-branch-modal-shell">
                        {this.renderStepTabs()}
                        <div className="new-branch-content">
                            {this.state.errorMessage && <Alert color="danger" className="new-branch-error">{this.state.errorMessage}</Alert>}
                            {this.renderActiveStep()}
                            {loading && (
                                <div className="new-branch-loading">
                                    <Progress animated color="info" value="100" />
                                </div>
                            )}
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter className="new-branch-modal-footer">
                    {this.renderStepFooter(isFinalStep)}
                </ModalFooter>
            </Modal>
        )
    }
}


//-----------------------------------
export default graphql(AllTenants, {
    options: { fetchPolicy: 'network-only' },
    props: ({ data: { allTenants, loading } }) => ({
        allTenants,
        tenantsLoading: loading
    })
})(
    graphql(AllBranches, {
        options: { fetchPolicy: 'network-only' },
        props: ({ data: { allBranches, loading } }) => ({
            allBranches,
            branchesLoading: loading
        })
    })(
        graphql(AllCarDocumentTypesQuery, {
            options: { fetchPolicy: 'network-only' },
            props: ({ data: { loading, allCarDocumentTypes } }) => ({
                carDocumentTypesLoading: loading,
                allCarDocumentTypes
            })
        })(
            graphql(AllDriverDocumentTypesQuery, {
                options: { fetchPolicy: 'network-only' },
                props: ({ data: { loading, allDriverDocumentTypes } }) => ({
                    driverDocumentTypeLoading: loading,
                    allDriverDocumentTypes
                })
            })(
                graphql(BranchSettingKeyQuery, {
                    options: { fetchPolicy: 'network-only' },
                    props: ({ data: { allBranchSettingKeys, loading } }) => ({
                        allBranchSettingKeys,
                        loading
                    })
                })(
                    graphql(BranchLegalEntity, {
                        options: { fetchPolicy: 'network-only' },
                        props: ({ data: { BranchLegalEntity } }) => ({
                            BranchLegalEntity
                        })
                    })(
                        graphql(AllLineOfBusinessQuery, {
                            options: { fetchPolicy: 'network-only' },
                            props: ({ data: { loading, allLineOfBusiness } }) => ({
                                allLineOfBusinessLoading: loading,
                                allLineOfBusiness
                            })
                        })(withApolloClient(NewBranchModal))
                    )
                )
            )
        )
    )
);
